import { DeleteOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Layout,
  Modal,
  Result,
  Select,
  Skeleton,
  Spin,
  Table,
  notification
} from "antd";
import { Option } from "antd/es/mentions";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiAccessAccountList, apiAccessList, apiCreateAccessAccount, apiDeleteAccessAccount, apiUpdateAccessAccount } from '../../../../../api/access';
import { apiGroupsList } from '../../../../../api/groups';
import { formatDate } from "../../../../../utils/format";
import { useIsMobileHook } from "../../../../../utils/WindowHooks";
import AccessCreateModal from "../../../access/AccessCreate/AccessCreate";
import EditAccessModal from "./EditAccessModal";
import './AccessAccount.scss';

const { Content } = Layout;

const AccessAccount = ({ dataInfo }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const isMobile = useIsMobileHook(1600);
  const [loading, setLoading] = useState(true);
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleRowClick = (record) => {
    const id = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(id);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(id);
    }
    setExpandedRowKeys(newExpandedRowKeys);
  };

  const getAccesses = async (page) => {
    setLoading(true);
    try {
      const json = await apiAccessAccountList(dataInfo.id);
      setData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
      }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setActiveError(true);
      if (error.status === 400) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao listar os acessos.",
          placement: "topRight",
        });
        setErrorStatus("404");
      } else if (error.status === 500) {
        notification.error({
          message: "Erro",
          description:
            "Ocorreu um erro no servidor ao listar os acessos. Por favor, tente novamente mais tarde.",
          placement: "topRight",
        });
        setErrorStatus("500");
      } else {
        setErrorStatus("404");
        notification.error("Erro ao buscar os acessos.");
      }
    }
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  useEffect(() => {
    getAccesses();
  }, []);

  const columns = [
    {
      title: "Nome",
      dataIndex: ["user", "username"],
      key: "user_name",
      render: (username) => (
        <p dangerouslySetInnerHTML={{ __html: username }} />
      ),
      responsive: ["xl"],
    },
    {
      title: "Email",
      dataIndex: ["user", "email"],
      key: "user_email",
      render: (user_email) => (
        <p className="truncate-text" dangerouslySetInnerHTML={{ __html: user_email }} />
      ),
    },
    {
      title: "Data de Entrada",
      dataIndex: "created_at",
      key: "date_joined",
      render: (date_joined) => (
        <p dangerouslySetInnerHTML={{ __html: new Date(date_joined).toLocaleDateString() }} />
      ),
      responsive: ["xl"],
    },
    {
      title: "Perfil de Acesso",
      dataIndex: ["group", "name"],
      key: "group_name",
      render: (group_name) => (
        <p style={{ textTransform: "capitalize" }} className={`badge badge-error`}>
          {group_name}
        </p>
      ),
      responsive: ["sm"],
    },
    {
      title: "Detalhes",
      dataIndex: "",
      render: (record) => (
        <Link to={`/admin/acessos/${record.user.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      ),
      key: "info",
      responsive: ["sm"],
    },
    {
      title: "Editar",
      dataIndex: "",
      render: (record) => (
        <EditOutlined onClick={() => handleEditAccess(record)} style={{ cursor: "pointer", height: "100%" }} />
      ),
      key: "edit",
      responsive: ["sm"],
    },
    {
      title: "Excluir",
      dataIndex: "",
      render: (record) => (
        <DeleteOutlined onClick={() => confirmDelete(record)} style={{ cursor: "pointer", height: "100%" }} />
      ),
      key: "delete",
      responsive: ["sm"],
    },
  ];

  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  const confirmDelete = (record) => {
    Modal.confirm({
      title: 'Tem certeza que deseja deletar?',
      content: 'Essa ação não pode ser desfeita.',
      okText: 'Deletar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => handleDelete(record?.user?.id, record?.id),
    });
  };

  const handleDelete = async (idUser, accessId) => {
    try {
      await apiDeleteAccessAccount(idUser, accessId).then(json => {
        notification.success({
          message: "Acesso deletado com sucesso",
          description: "Acesso deletado com sucesso",
          placement: "topRight",
        });
        setIsModalVisible(false);
        setIsSecondModalVisible(false)
        getAccesses();
      })
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao deletar o acesso.",
        placement: "topRight",
      });
    }
  };

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
        Nome:{" "}
        <p dangerouslySetInnerHTML={{ __html: record.user.username }} />
      </span>
      <span>
        Perfil de Acesso:{" "}
        <p style={{ textTransform: "capitalize" }} className={`badge ${record.is_superuser ? "badge-success" : "badge-error"}`}>
          {record.is_superuser ? "Super Usuário" : "Vendedor"}
        </p>
      </span>
      <span>
        Email:{" "}
        <p
          className="truncate-text"
          dangerouslySetInnerHTML={{ __html: record.user.email }}
        />
      </span>
      <span>
        Entrada no sistema:{" "}
        <p dangerouslySetInnerHTML={{ __html: formatDate(record.created_at) }} />
      </span>
      <span style={{ marginTop: "10px" }}>
        Informação:{" "}
        <Link to={`/admin/acessos/${record.user.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      </span>
      <span style={{ marginTop: "10px" }}>
        Editar:{" "}
        <EditOutlined onClick={() => handleEditAccess(record)} style={{ cursor: "pointer", height: "100%" }} />
      </span>
      <span style={{ marginTop: "10px" }}>
        Deletar:{" "}
        <DeleteOutlined onClick={() => confirmDelete(record)} style={{ cursor: "pointer", height: "100%" }} />
      </span>
    </div>
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSecondModalVisible, setIsSecondModalVisible] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    setOpenCreateModal(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsSecondModalVisible(true)
  };

  const fetchOptions = async (search) => {
    setModalLoading(true);
    try {
      const json = await apiAccessList({ search });
      setOptions(json.results.map(item => ({ label: item.email, value: item.id })));
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Ocorreu um erro ao buscar os dados.',
        placement: 'topRight',
      });
    } finally {
      setModalLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await apiGroupsList();
      setGroups(response.results || []);
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Erro ao carregar os grupos disponíveis.',
        placement: 'topRight',
      });
    }
  };

  useEffect(() => {
    if (searchText !== '') {
      fetchOptions(searchText);
    } else {
      setOptions([]);
    }
  }, [searchText]);

  useEffect(() => {
    if (isSecondModalVisible) {
      fetchGroups();
    }
  }, [isSecondModalVisible]);

  const handleSecondModalCancel = () => {
    setIsSecondModalVisible(false);
    setSelectedGroupId(null);
    setOptions([]);
    setSearchText('');
  };

  const handleCreateAccount = async () => {
    if (!options[0]?.value) {
      notification.warning({
        message: 'Atenção',
        description: 'Por favor, selecione um usuário.',
        placement: 'topRight',
      });
      return;
    }

    if (!selectedGroupId) {
      notification.warning({
        message: 'Atenção',
        description: 'Por favor, selecione um grupo.',
        placement: 'topRight',
      });
      return;
    }

    try {
      await apiCreateAccessAccount(options[0].value, dataInfo.id, selectedGroupId).then(json => {
        notification.success({
          message: "Acesso criado com sucesso",
          description: "Acesso criado com sucesso",
          placement: "topRight",
        });
        setIsModalVisible(false);
        setIsSecondModalVisible(false)
        setSelectedGroupId(null);
        setOptions([]);
        setSearchText('');
        getAccesses();
      })
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao criar o acesso.",
        placement: "topRight",
      });
    }
  };

  const onCreate = async (id, groupId) => {
    try {
      await apiCreateAccessAccount(id, dataInfo.id, groupId).then(json => {
        notification.success({
          message: "Acesso vinculado com sucesso",
          description: "Acesso vinculado com sucesso",
          placement: "topRight",
        });
        getAccesses();
      })
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao vincular o acesso.",
        placement: "topRight",
      });
    }
  }

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedAccessRecord, setSelectedAccessRecord] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const handleEditAccess = (record) => {
    setSelectedAccessRecord(record);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    getAccesses();
  };

  return (
    <div className="AccessAccount">
      {activeError ? (
        renderError(errorStatus)
      ) : (
        <>
          <Layout
            style={{ backgroundColor: 'white' }}
          >
            <Breadcrumb
              style={{
                margin: "16px 0",
              }}
            >
              <p class="subtitle-page">Usuários com acesso</p>
              <Divider />
            </Breadcrumb>
            <Content
              style={{
                margin: 0,
                minHeight: 280,
              }}
            >
              <div className="box-divider">
                <Button onClick={showModal} type="primary">
                  Adicionar novo acesso
                </Button>
              </div>
              <Divider />
              {loading ? (
                <Skeleton active />
              ) : (
                <Table
                  columns={columns}
                  locale={locale}
                  dataSource={data.map((item) => ({ ...item, key: item.id }))}
                  expandable={{
                    expandedRowRender: isMobile ? expandedRowRender : undefined,
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (expanded, record) => handleRowClick(record),
                    responsive: ["xl"],
                  }}
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    showSizeChanger: false,
                  }}
                  bordered
                />
              )}
            </Content>
            <Divider />
          </Layout>
          <Modal
            title="Escolha uma opção"
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={() => setIsModalVisible(false)}
            footer={null}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="primary"
                onClick={handleOk}
                style={{ marginRight: '8px', width: '100%' }}
              >
                Novo usuário
              </Button>
              <Button
                type="default"
                style={{ width: '100%' }}
                onClick={handleCancel}
              >
                Usuário existente
              </Button>
            </div>
          </Modal>
          <Modal
            title="Adicionar Usuário Existente"
            visible={isSecondModalVisible}
            onCancel={handleSecondModalCancel}
            footer={null}
          >
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <strong>Selecione o Usuário:</strong>
              </label>
              <Select
                showSearch
                placeholder="Selecione um usuário"
                notFoundContent={modalLoading ? <Spin size="small" /> : null}
                filterOption={false}
                onSearch={value => setSearchText(value)}
                style={{ width: '100%' }}
              >
                {options.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <strong>Selecione o Grupo:</strong>
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Selecione um grupo"
                value={selectedGroupId}
                onChange={setSelectedGroupId}
                notFoundContent={'Nenhum grupo encontrado'}
              >
                {groups.map(group => (
                  <Option key={group.id} value={group.id}>
                    {group.name}
                  </Option>
                ))}
              </Select>
            </div>
            
            <Button
              type="primary"
              onClick={handleCreateAccount}
              style={{ marginRight: '8px' }}
            >
              Confirmar
            </Button>
            <Button
              type="default"
              onClick={handleSecondModalCancel}
            >
              Cancelar
            </Button>
          </Modal>
          <AccessCreateModal onCreate={onCreate} fetchPage={getAccesses} visible={openCreateModal} onClose={() => setOpenCreateModal(false)} />
          <EditAccessModal 
            visible={editModalVisible} 
            onClose={() => setEditModalVisible(false)} 
            accessRecord={selectedAccessRecord} 
            onSuccess={handleEditSuccess} 
          />
        </>
      )}
    </div>
  );
};

export default AccessAccount;
